
import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertCircle, RefreshCw, WifiOff, ExternalLink } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    retryCount: 0
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error);
    console.log("Error message:", error.message);
    console.log("Error stack:", error.stack);
    console.log("Component stack:", errorInfo.componentStack);
    
    this.setState({ errorInfo });
    
    // Show toast for better user experience
    const isNetworkError = this.isNetworkError(error.message);
    
    toast({
      title: isNetworkError ? "Network Connection Issue" : "Something went wrong",
      description: isNetworkError 
        ? "Unable to connect to our services. Please check your internet connection."
        : "An unexpected error occurred. Please try again.",
      variant: "destructive",
    });
  }

  isNetworkError = (errorMessage: string): boolean => {
    if (!errorMessage) return false;
    
    const lowerCaseError = errorMessage.toLowerCase();
    return (
      lowerCaseError.includes("failed to fetch") || 
      lowerCaseError.includes("networkerror") ||
      lowerCaseError.includes("network") ||
      lowerCaseError.includes("edge function") ||
      lowerCaseError.includes("timed out") ||
      lowerCaseError.includes("cors") ||
      lowerCaseError.includes("failed to send") ||
      lowerCaseError.includes("connection") ||
      lowerCaseError.includes("unable to connect") ||
      lowerCaseError.includes("internet") ||
      lowerCaseError.includes("offline") ||
      lowerCaseError.includes("econnrefused") ||
      lowerCaseError.includes("econnreset")
    );
  }

  handleRetry = (): void => {
    this.setState(prevState => {
      const newRetryCount = prevState.retryCount + 1;
      
      // Show toast for retry
      toast({
        title: `Retry attempt ${newRetryCount}`,
        description: "Trying again...",
      });
      
      return {
        hasError: false, 
        error: null, 
        errorInfo: null,
        retryCount: newRetryCount
      };
    });
  };

  handleRefresh = (): void => {
    window.location.reload();
  };

  renderNetworkErrorDetails = (errorMessage: string) => {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 text-red-600">
          <WifiOff className="h-6 w-6" />
          <h3 className="text-lg font-medium">Network Connection Error</h3>
        </div>
        
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unable to connect to services</AlertTitle>
          <AlertDescription>
            We're having trouble connecting to our AI services. This appears to be a network connectivity issue.
          </AlertDescription>
        </Alert>
        
        <div className="text-sm text-gray-700 space-y-3">
          <p className="font-medium">The issue could be due to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Your internet connection is unstable or disconnected</li>
            <li>A firewall or VPN is blocking access to our services</li>
            <li>The AI service is temporarily unavailable</li>
            <li>Browser extensions interfering with network requests</li>
            <li>DNS resolution issues on your network</li>
            <li>Corporate or public network restrictions</li>
          </ul>
        </div>
        
        <div className="text-sm text-gray-700 space-y-3">
          <p className="font-medium">Try these troubleshooting steps:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Verify your internet connection</strong> - Check that other websites load properly</li>
            <li><strong>Turn off VPN or proxy</strong> - These can sometimes interfere with API connections</li>
            <li><strong>Try a different network</strong> - Switch from WiFi to mobile data or vice versa</li>
            <li><strong>Clear browser cache</strong> - This can resolve issues with cached network errors</li>
            <li><strong>Try another browser</strong> - Some browsers handle network requests differently</li>
            <li><strong>Wait a few minutes</strong> - The service may be temporarily unavailable</li>
          </ul>
        </div>
        
        {errorMessage && (
          <div className="text-xs text-gray-500 p-2 bg-gray-100 rounded overflow-auto mt-2">
            <details>
              <summary className="cursor-pointer">Technical Error Details</summary>
              <p className="mt-2 font-mono whitespace-pre-wrap break-words">{errorMessage}</p>
            </details>
          </div>
        )}
        
        <div className="flex flex-wrap gap-3 mt-4">
          <Button
            onClick={this.handleRetry}
            className="flex items-center gap-2"
            variant="outline"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
          <Button
            onClick={this.handleRefresh}
          >
            Refresh Page
          </Button>
        </div>
      </div>
    );
  };

  renderGenericErrorDetails = (errorMessage: string) => {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 text-red-600">
          <AlertCircle className="h-6 w-6" />
          <h3 className="text-lg font-medium">Something went wrong</h3>
        </div>
        
        <p className="text-gray-700">
          An unexpected error occurred while processing your request.
        </p>
        
        <div className="text-sm text-gray-700 space-y-3">
          <p className="font-medium">Try these steps:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Try refreshing the page</li>
            <li>Clear your browser cache</li>
            <li>Try again in a few moments</li>
          </ul>
        </div>
        
        {errorMessage && (
          <div className="text-xs text-gray-500 p-2 bg-gray-100 rounded overflow-auto mt-2">
            <details>
              <summary className="cursor-pointer">Technical Error Details</summary>
              <p className="mt-2 font-mono whitespace-pre-wrap break-words">{errorMessage}</p>
            </details>
          </div>
        )}
        
        <div className="flex flex-wrap gap-3 mt-4">
          <Button
            onClick={this.handleRetry}
            className="flex items-center gap-2"
            variant="outline"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
          <Button
            onClick={this.handleRefresh}
          >
            Refresh Page
          </Button>
        </div>
      </div>
    );
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      const errorMessage = this.state.error?.message || "Unknown error";
      const isNetworkError = this.isNetworkError(errorMessage);
      
      return (
        <div className="p-6 border border-red-200 rounded-lg bg-slate-50 shadow-sm">
          {isNetworkError 
            ? this.renderNetworkErrorDetails(errorMessage)
            : this.renderGenericErrorDetails(errorMessage)
          }
          
          <div className="text-xs text-gray-500 mt-6 pt-4 border-t border-gray-200">
            <p>
              If you continue to experience issues, please try again later or contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
